/**
 * WebSocket hook (Sprint 13).
 *
 * Tenant-aware: backend Channels consumer JWT'den tenant'ı çıkarır.
 * Frontend sadece URL'i bilir.
 *
 * Kullanım:
 *   const { lastMessage, status } = useWebSocket('/ws/kds/station/GRILL/');
 */
import { useEffect, useRef, useState } from "react";

export type WSStatus = "CONNECTING" | "OPEN" | "CLOSING" | "CLOSED";

// Heartbeat: idle bağlantı load balancer / proxy / NAT tarafından "ölü"
// sanılıp kapatılmasın diye periyodik ping. Backend HeartbeatMixin
// {"type":"ping"} → {"type":"pong"} ile yanıtlar.
//   - PING_INTERVAL_MS: ne sıklıkla ping atılacağı. Tipik LB idle
//     timeout'u 60-90 sn → 25 sn rahatça altında kalır.
//   - PONG_TIMEOUT_MS: ping sonrası pong gelmezse bağlantı ölü sayılır
//     ve kapatılır → onclose backoff ile yeniden bağlanır. Bu, browser'ın
//     "yarı açık" (half-open) TCP'yi fark etmediği durumu da yakalar.
const PING_INTERVAL_MS = 25_000;
const PONG_TIMEOUT_MS = 10_000;

export function useWebSocket<T = any>(path: string | null) {
  const [status, setStatus] = useState<WSStatus>("CLOSED");
  const [lastMessage, setLastMessage] = useState<T | null>(null);
  const [messages, setMessages] = useState<T[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);

  useEffect(() => {
    if (!path) return;

    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let pingTimer: ReturnType<typeof setInterval> | null = null;
    let pongTimer: ReturnType<typeof setTimeout> | null = null;

    function clearHeartbeat() {
      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
      if (pongTimer) {
        clearTimeout(pongTimer);
        pongTimer = null;
      }
    }

    function startHeartbeat(ws: WebSocket) {
      clearHeartbeat();
      pingTimer = setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) return;
        try {
          ws.send(JSON.stringify({ type: "ping" }));
        } catch {
          return;
        }
        // Pong watchdog: süresi içinde pong gelmezse bağlantı ölü →
        // kapat, onclose yeniden bağlanır.
        if (pongTimer) clearTimeout(pongTimer);
        pongTimer = setTimeout(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.close(4002, "pong timeout");
          }
        }, PONG_TIMEOUT_MS);
      }, PING_INTERVAL_MS);
    }

    function connect() {
      if (cancelled) return;
      const token = localStorage.getItem("auth_token");
      const wsBase =
        import.meta.env["VITE_WS_URL"] ?? "ws://localhost:8000";
      // Token Sec-WebSocket-Protocol header'ı üzerinden taşınır; URL'e
      // query string olarak EKLENMEZ. Aksi halde token tarayıcı konsoluna,
      // proxy access log'larına ve performance entry API'sine sızar.
      // Backend (JWTAuthMiddleware) iki subprotocol bekler:
      //   ["jwt.bearer.v1", "<access_token>"]
      // ve handshake'i "jwt.bearer.v1" ile kabul eder.
      const url = `${wsBase}${path}`;
      const protocols = token ? ["jwt.bearer.v1", token] : undefined;
      const ws = new WebSocket(url, protocols);
      wsRef.current = ws;
      setStatus("CONNECTING");

      ws.onopen = () => {
        setStatus("OPEN");
        reconnectAttempts.current = 0;
        startHeartbeat(ws);
      };

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          // Pong: heartbeat yanıtı — watchdog'u iptal et, UI'a yansıtma.
          if (data?.type === "pong") {
            if (pongTimer) {
              clearTimeout(pongTimer);
              pongTimer = null;
            }
            return;
          }
          setLastMessage(data);
          setMessages((prev) => [...prev.slice(-99), data]);
        } catch {
          // ignore parse errors
        }
      };

      ws.onerror = () => {
        // close handler ile devam
      };

      ws.onclose = () => {
        setStatus("CLOSED");
        clearHeartbeat();
        if (!cancelled) {
          // exponential backoff
          const delay = Math.min(
            1000 * 2 ** reconnectAttempts.current,
            30000,
          );
          reconnectAttempts.current += 1;
          reconnectTimer = setTimeout(connect, delay);
        }
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      clearHeartbeat();
      const ws = wsRef.current;
      if (!ws) return;
      // React 18 StrictMode dev'de bileşenler mount → unmount → re-mount
      // edildiğinden WS henüz handshake bitmeden close() çağrılıyor ve
      // console "WebSocket is closed before the connection is established"
      // hatası basıyor (zararsız ama gürültülü). CONNECTING durumundaysa
      // önce onopen'i bekle, sonra düzgün close et. OPEN ise direkt close.
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.addEventListener(
          "open",
          () => ws.close(1000, "unmount"),
          { once: true },
        );
        // onerror'u sustur — unmount sırasındaki abort'u "failed" olarak
        // log'lamasın. Production'da StrictMode kapalı olduğu için bu
        // patika çalışmaz; sadece dev gürültüsünü sustur.
        ws.onerror = null;
      } else if (ws.readyState === WebSocket.OPEN) {
        ws.close(1000, "unmount");
      }
    };
  }, [path]);

  return {
    status,
    lastMessage,
    messages,
    send: (data: any) => wsRef.current?.send(JSON.stringify(data)),
  };
}
